/**
 * Session (D-044): the access token (15 min by default) lives only in memory; the refresh token
 * lives in an httpOnly cookie that JavaScript cannot read. Only the chosen agency is remembered.
 */

import { configureApi, refreshSession } from "@agencyos/api-client";
import { useSyncExternalStore } from "react";

import i18next from "./i18n";
import { readStorage, writeStorage } from "./storage";

const ORGANIZATION_KEY = "agencyos.org";

interface SessionState {
  accessToken: string | null;
  organization: string | null;
  /** false until the first silent refresh attempt has answered. */
  ready: boolean;
}

let state: SessionState = {
  accessToken: null,
  organization: readStorage(ORGANIZATION_KEY),
  ready: false,
};
const listeners = new Set<() => void>();

function update(next: Partial<SessionState>): void {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener());
}

export const session = {
  get: (): SessionState => state,
  signIn(accessToken: string, organization: string | null): void {
    writeStorage(ORGANIZATION_KEY, organization);
    update({ accessToken, organization, ready: true });
  },
  setOrganization(organization: string | null): void {
    writeStorage(ORGANIZATION_KEY, organization);
    update({ organization });
  },
  signOut(): void {
    writeStorage(ORGANIZATION_KEY, null);
    update({ accessToken: null, organization: null, ready: true });
  },
  /** Back to the state of a freshly loaded page (tests). */
  reset(): void {
    update({ accessToken: null, organization: readStorage(ORGANIZATION_KEY), ready: false });
  },
  /** On page load: reuse the cookie session if it is still valid. */
  async restore(): Promise<void> {
    const ok = await refreshSession();
    update({ ready: true, ...(ok ? {} : { accessToken: null }) });
  },
};

export function useSession(): SessionState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

configureApi({
  baseUrl: import.meta.env.VITE_API_URL ?? "",
  client: "web",
  getAccessToken: () => state.accessToken,
  setAccessToken: (accessToken) => update({ accessToken }),
  getOrganization: () => state.organization,
  getLanguage: () => i18next.language,
  onSessionExpired: () => session.signOut(),
});
